<template>
    <div class="media-browser">
        <div class="side-menu" v-if="!type">
            <folder-tree
                :folders="folders"
                :file-counts="folderFileCounts"
                :view="view"
                :folder="folder"
                :is-dragging-folder="draggingFolderId !== null"
                @select="onTreeSelect"
                @create-folder="newFolder"
                @rename-folder="renameFolderPrompt"
                @delete-folder="deleteFolderFromTree"
                @drop-items="onTreeDrop"
                @dragstart-folder="onFolderDragStart"
                @dragend-folder="onFolderDragEnd"
            />
        </div>
        <div class="view view-loading" v-if="!loaded">
            <i class="fas fa-spinner fa-spin-pulse fa-2x mb-3"></i>
            <div class="is-size-6 has-text-grey">Loading media library…</div>
        </div>
        <div class="view" v-else>
            <card-toolbar>
                <span class="is-size-7 select-count">
                    <b-checkbox
                        v-if="selectionEnabled"
                        :value="allVisibleSelected"
                        :indeterminate="someVisibleSelected"
                        @input="toggleSelectAllVisible"
                        class="mr-2"
                        :title="allVisibleSelected ? 'Deselect all visible' : 'Select all visible'"
                    />
                    <span v-if="selectedCount">{{ selectedCount }} selected of </span>
                    <span>{{media.length}} Items ({{ viewSubtitle }})</span>
                    <i v-if="libraryLoading || trashLoading"
                       class="fas fa-spinner fa-spin-pulse ml-2 has-text-grey"
                       title="Refreshing…"></i>
                </span>
                <template v-slot:right>
                    <template v-if="!isTrashView">
                        <b-dropdown aria-role="menu" position="is-bottom-left" append-to-body :disabled="toolsBusy">
                            <template #trigger>
                                <button :disabled="toolsBusy">
                                    <i class="fas" :class="toolsBusy ? 'fa-spinner fa-spin-pulse' : 'fa-toolbox'"></i>
                                    {{ toolsBusyLabel || 'Tools' }}
                                    <i class="fas fa-caret-down ml-2" v-if="!toolsBusy"></i>
                                </button>
                            </template>
                            <b-dropdown-item @click="openPptxPicker">
                                <i class="fad fa-fw fa-file-powerpoint mr-2"></i>Extract PowerPoint
                            </b-dropdown-item>
                            <b-dropdown-item @click="openTransloadPrompt">
                                <i class="fad fa-fw fa-globe mr-2"></i>Transload File from URL
                            </b-dropdown-item>
                        </b-dropdown>
                        <button @click="uploadVisible = !uploadVisible" :class="{'is-active': uploadVisible}">
                            <i class="fas fa-upload"></i>{{ uploadVisible ? 'Hide Upload' : 'Upload File' }}
                        </button>
                        <input ref="pptxInput" type="file" accept=".pptx,.ppt" class="pptx-hidden-input" @change="onPptxFileSelected">
                    </template>
                    <button v-else @click="loadDeleted(true)" :disabled="trashLoading">
                        <i class="fas fa-rotate" :class="{'fa-spin': trashLoading}"></i>Refresh Trash
                    </button>
                </template>
            </card-toolbar>
            <card-container v-if="uploadVisible && !isTrashView" class="p-0">
                <upload-queue
                    :provider="provider"
                    :folder="folder"
                    @uploaded="onUploaded"
                />
            </card-container>
            <div v-if="pptxExtracting" class="tool-banner">
                <i class="fas fa-spinner fa-spin-pulse mr-3"></i>
                <div>
                    <strong>Extracting slides from {{ pptxExtractingName }}…</strong>
                    <div class="is-size-7 has-text-grey">This can take a minute or two depending on the deck size. Please don't close this tab.</div>
                </div>
                <b-progress v-if="pptxUploadPercent !== null && pptxUploadPercent < 100"
                            size="is-small" type="is-info" :value="pptxUploadPercent" class="tool-banner-progress" />
            </div>
            <div v-if="transloading" class="tool-banner">
                <i class="fas fa-spinner fa-spin-pulse mr-3"></i>
                <div>
                    <strong>Transloading file…</strong>
                    <div class="is-size-7 has-text-grey">Downloading from source and processing. This may take a moment.</div>
                </div>
            </div>
            <card-container class="p-2">
                <div class="columns pt-1 pb-1">
                    <div class="column is-5" v-if="orgProvider">
                        <b-field>
                            <b-select expanded placeholder="Content Provider" icon="person-chalkboard" v-model="provider">
                                <option v-for="p in providers" :value="p.id">
                                    {{ p.title }}
                                </option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="column is-narrow">
                        <b-field>
                            <b-select v-model="typeFilter" icon="photo-film-music" :disabled="isTrashView">
                                <option value="ALL">All Types</option>
                                <option value="PHOTO">Photos</option>
                                <option value="VIDEO">Videos</option>
                                <option value="AUDIO">Audio</option>
                                <option value="FILE">Documents</option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="column is-narrow">
                        <b-field>
                            <b-select v-model="sortBy" icon="sort">
                                <option value="newest">Newest First</option>
                                <option value="oldest">Oldest First</option>
                                <option value="name_asc">Name (A–Z)</option>
                                <option value="name_desc">Name (Z–A)</option>
                                <option value="size_desc">Largest First</option>
                                <option value="size_asc">Smallest First</option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="column is-narrow">
                        <div class="buttons has-addons view-toggle">
                            <button class="button" :class="{'is-primary is-selected': viewMode === 'grid'}"
                                    @click="viewMode = 'grid'" title="Grid view">
                                <i class="fas fa-grip"></i>
                            </button>
                            <button class="button" :class="{'is-primary is-selected': viewMode === 'list'}"
                                    @click="viewMode = 'list'" title="List view">
                                <i class="fas fa-list"></i>
                            </button>
                        </div>
                    </div>
                    <div class="column">
                        <b-field addons>
                            <b-input placeholder="Search..."
                                     type="search"
                                     icon="search"
                                     v-model="search"
                                     expanded
                            ></b-input>
                            <p class="control">
                                <button class="button is-primary">
                                    <b-icon icon="search" />
                                </button>
                            </p>
                        </b-field>
                    </div>
                </div>
            </card-container>
            <card-list>
                <separator-list-item />
            </card-list>
            <div class="media-scroller" ref="scroller">
                <div v-if="isTrashView && trashLoading && !trashLoaded" class="empty-state has-text-grey p-5 has-text-centered">
                    <i class="fas fa-spinner fa-spin-pulse fa-2x mb-3"></i>
                    <div>Loading trash…</div>
                </div>
                <div v-else-if="!media.length" class="empty-state has-text-grey p-5 has-text-centered">
                    <i class="fad fa-folder-open fa-2x mb-3"></i>
                    <div v-if="isTrashView">Trash is empty.</div>
                    <div v-else>No media matches your filters.</div>
                </div>
                <recycle-scroller
                    v-else-if="viewMode === 'grid' && gridColumns > 0"
                    key="grid-scroller"
                    class="scroller"
                    :items="media"
                    :item-size="gridRowHeight"
                    :grid-items="gridColumns"
                    :item-secondary-size="gridCellWidth"
                    :buffer="scrollerBuffer"
                    key-field="id"
                    v-slot="{ item: m }"
                >
                    <div class="card media-card"
                         :class="{'is-clickable': true, 'is-selected': isSelected(m.id), 'is-dragging': draggingItemIds.indexOf(m.id) !== -1, 'is-active': detailsTarget && detailsTarget.id === m.id}"
                         :draggable="!isTrashView && !type"
                         @dragstart="onItemDragStart($event, m)"
                         @dragend="onItemDragEnd"
                         @click="onItemClick(m)">
                        <div class="columns is-gapless mb-1 mt-1">
                            <div class="column">
                                <div class="truncate mt-2">
                                    <b-tooltip :title="m.title">
                                        <i class="fad fa-fw mr-1 ml-2" :class="{'fa-image': m.type === 'PHOTO', 'fa-film': m.type === 'VIDEO', 'fa-file': m.type === 'FILE'}"></i>
                                        {{ m.title }}
                                    </b-tooltip>
                                </div>
                            </div>
                            <div class="column is-narrow" @click.stop>
                                <b-dropdown
                                    append-to-body
                                    aria-role="menu"
                                    scrollable
                                    max-height="200"
                                    trap-focus
                                >
                                    <template #trigger>
                                        <a class="navbar-item" role="button">
                                            <i class="fas fa-ellipsis-vertical "></i>
                                        </a>
                                    </template>
                                    <a :href="m.url" target="_blank" class="has-text-grey-dark">
                                        <b-dropdown-item>
                                            <i class="fad fa-fw fa-arrow-up-right-from-square mr-2"></i>View Raw File
                                        </b-dropdown-item>
                                    </a>
                                    <b-dropdown-item @click="downloadFile(m)">
                                        <i class="fad fa-fw fa-download mr-2"></i>Download File
                                    </b-dropdown-item>
                                    <template v-if="isTrashView">
                                        <b-dropdown-item @click="restoreFile(m)">
                                            <i class="fad fa-fw fa-rotate-left mr-2"></i>Restore File
                                        </b-dropdown-item>
                                    </template>
                                    <template v-else>
                                        <b-dropdown-item @click="requestDelete(m)">
                                            <i class="fad fa-fw fa-trash mr-2"></i>Delete File
                                        </b-dropdown-item>
                                        <hr class="dropdown-divider" aria-role="menuitem">
                                        <section v-if="folders.length">
                                            <b-dropdown-item @click="moveToFolder(m, {id: null})" v-if="m.folder_id">
                                                <i class="fad fa-fw fa-folder-xmark mr-2"></i>Remove from <span class="has-text-weight-bold">'{{ folders.find(f => f.id === m.folder_id).title }}'</span>
                                            </b-dropdown-item>
                                            <b-dropdown-item v-for="f in folders" @click="moveToFolder(m, f)">
                                                <i class="fad fa-fw fa-folder-arrow-up mr-2"></i>Move to <span class="has-text-weight-bold">'{{ f.title }}'</span>
                                            </b-dropdown-item>
                                        </section>
                                        <b-dropdown-item v-else class="is-italic" disabled>
                                            <i class="fad fa-fw fa-folder-xmark mr-2"></i>No Folders Available
                                        </b-dropdown-item>
                                    </template>
                                </b-dropdown>
                            </div>
                        </div>
                        <div class="card-image">
                            <figure class="image is-4by3">
                                <img :src="m.thumbnail" width="320" v-if="m.type !== 'FILE'" class="m-0 p-0">
                            </figure>
                            <button v-if="selectionEnabled"
                                    class="card-select-btn"
                                    :class="{'is-active': isSelected(m.id)}"
                                    :title="isSelected(m.id) ? 'Deselect' : 'Select'"
                                    @click.stop="toggleSelection(m.id)">
                                <i class="fas" :class="isSelected(m.id) ? 'fa-square-check' : 'fa-square'"></i>
                            </button>
                        </div>
                        <div class="card-content pt-3 subtitle is-7 has-text-grey pl-2">
                            <div v-if="m.folder_id"><i class="fad fa-fw fa-folder mr-2"></i>{{ findFolderTitle(m.folder_id) }}</div>
                            <div v-if="isTrashView && m.deleted_at"><i class="fad fa-fw fa-trash mr-2"></i>Deleted {{ m.deleted_at | moment('MM/DD/YYYY') }}</div>
                            <div><i class="fad fa-fw fa-calendar mr-2"></i>{{ m.created_at | moment('MM/DD/YYYY H:MM') }}</div>
                            <div v-if="m.type === 'FILE'"><i class="fad fa-fw fa-file mr-2"></i>{{ m.size | formatSize }}</div>
                            <div v-if="m.type === 'VIDEO'"><i class="fad fa-fw fa-ruler-combined mr-2"></i>{{ m.width }}x{{ m.height }}, 00:{{ (m.duration / 1000) | moment('utc', 'mm:ss') }}, {{ m.size | formatSize }}</div>
                            <div v-if="m.type === 'PHOTO'"><i class="fad fa-fw fa-ruler-combined mr-2"></i>{{ m.width }}x{{ m.height }}, {{ m.size | formatSize }}</div>
                        </div>
                    </div>
                </recycle-scroller>
                <recycle-scroller
                    v-else-if="viewMode === 'list'"
                    key="list-scroller"
                    class="scroller"
                    :items="media"
                    :item-size="listRowHeight"
                    :buffer="scrollerBuffer"
                    key-field="id"
                    v-slot="{ item: m }"
                >
                    <div class="media-row"
                         :class="{'is-clickable': true, 'is-selected': isSelected(m.id), 'is-dragging': draggingItemIds.indexOf(m.id) !== -1, 'is-active': detailsTarget && detailsTarget.id === m.id}"
                         :draggable="!isTrashView && !type"
                         @dragstart="onItemDragStart($event, m)"
                         @dragend="onItemDragEnd"
                         @click="onItemClick(m)">
                        <div v-if="selectionEnabled" class="media-row-select" @click.stop>
                            <b-checkbox :value="isSelected(m.id)" @input="toggleSelection(m.id)" />
                        </div>
                        <div class="media-row-thumb">
                            <img :src="m.thumbnail" v-if="m.thumbnail && m.type !== 'FILE'">
                            <i v-else class="fad"
                               :class="{'fa-file': m.type === 'FILE', 'fa-film': m.type === 'VIDEO', 'fa-music': m.type === 'AUDIO', 'fa-image': m.type === 'PHOTO'}"></i>
                        </div>
                        <div class="media-row-title">
                            <div class="truncate">{{ m.title }}</div>
                            <div class="is-size-7 has-text-grey">
                                <span v-if="m.folder_id"><i class="fad fa-fw fa-folder mr-1"></i>{{ findFolderTitle(m.folder_id) }} · </span>
                                <span v-if="isTrashView && m.deleted_at"><i class="fad fa-fw fa-trash mr-1"></i>Deleted {{ m.deleted_at | moment('MM/DD/YYYY') }} · </span>
                                <span>{{ m.created_at | moment('MM/DD/YYYY') }}</span>
                            </div>
                        </div>
                        <div class="media-row-meta is-size-7 has-text-grey">
                            <span v-if="m.width && m.height">{{ m.width }}×{{ m.height }}</span>
                            <span v-if="m.size">{{ m.size | formatSize }}</span>
                        </div>
                        <div class="media-row-actions" @click.stop>
                            <b-dropdown
                                append-to-body
                                aria-role="menu"
                                scrollable
                                max-height="200"
                                trap-focus
                                position="is-bottom-left"
                            >
                                <template #trigger>
                                    <a class="navbar-item" role="button">
                                        <i class="fas fa-ellipsis-vertical"></i>
                                    </a>
                                </template>
                                <a :href="m.url" target="_blank" class="has-text-grey-dark">
                                    <b-dropdown-item>
                                        <i class="fad fa-fw fa-arrow-up-right-from-square mr-2"></i>View Raw File
                                    </b-dropdown-item>
                                </a>
                                <b-dropdown-item @click="downloadFile(m)">
                                    <i class="fad fa-fw fa-download mr-2"></i>Download File
                                </b-dropdown-item>
                                <template v-if="isTrashView">
                                    <b-dropdown-item @click="restoreFile(m)">
                                        <i class="fad fa-fw fa-rotate-left mr-2"></i>Restore File
                                    </b-dropdown-item>
                                </template>
                                <template v-else>
                                    <b-dropdown-item @click="requestDelete(m)">
                                        <i class="fad fa-fw fa-trash mr-2"></i>Delete File
                                    </b-dropdown-item>
                                    <hr class="dropdown-divider" aria-role="menuitem">
                                    <section v-if="folders.length">
                                        <b-dropdown-item @click="moveToFolder(m, {id: null})" v-if="m.folder_id">
                                            <i class="fad fa-fw fa-folder-xmark mr-2"></i>Remove from <span class="has-text-weight-bold">'{{ folders.find(f => f.id === m.folder_id).title }}'</span>
                                        </b-dropdown-item>
                                        <b-dropdown-item v-for="f in folders" @click="moveToFolder(m, f)">
                                            <i class="fad fa-fw fa-folder-arrow-up mr-2"></i>Move to <span class="has-text-weight-bold">'{{ f.title }}'</span>
                                        </b-dropdown-item>
                                    </section>
                                    <b-dropdown-item v-else class="is-italic" disabled>
                                        <i class="fad fa-fw fa-folder-xmark mr-2"></i>No Folders Available
                                    </b-dropdown-item>
                                </template>
                            </b-dropdown>
                        </div>
                    </div>
                </recycle-scroller>
            </div>
            <bulk-action-bar
                v-if="selectionEnabled && selectedCount"
                :count="selectedCount"
                :folders="folders"
                :working="bulkWorking || bulkDownloading"
                :downloading="bulkDownloading"
                :mode="isTrashView ? 'trash' : 'active'"
                @move="bulkMove"
                @delete="bulkDelete"
                @download="bulkDownload"
                @restore="bulkRestore"
                @clear="clearSelection"
            />
            <file-details-panel
                v-if="detailsTarget"
                :file="detailsTarget"
                :folders="folders"
                @close="detailsTarget = null"
                @updated="onFileUpdated"
            />
        </div>

        <delete-confirm-modal
            v-if="deleteTarget"
            :file="deleteTarget"
            @cancel="deleteTarget = null"
            @deleted="onDeleted"
        />
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";

    import {client as http} from "@/http_client";
    import CardList from "../../TIER/components/CardList";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import SearchListItem from "../../TIER/components/SearchListItem";
    import SeparatorListItem from "../../TIER/components/SeparatorListItem";
    import CardFooter from "../../TIER/components/CardFooter";
    import CardContainer from "../../TIER/components/CardContainer";
    import UploadQueue from "./UploadQueue.vue";
    import DeleteConfirmModal from "./DeleteConfirmModal.vue";
    import BulkActionBar from "./BulkActionBar.vue";
    import FolderTree from "./FolderTree.vue";
    import FileDetailsPanel from "./FileDetailsPanel.vue";
    import async from "async";
    import {mapGetters} from "vuex";
    import { RecycleScroller } from 'vue-virtual-scroller';
    import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

    const GRID_CELL_WIDTH = 290;
    const GRID_ROW_HEIGHT = 360;
    const LIST_ROW_HEIGHT = 64;
    // How many px above/below the visible area to keep rendered. Default is 200.
    // Higher = smoother scroll, slightly more DOM. ~2 grid rows worth.
    const SCROLLER_BUFFER = 1000;
    const FOLDER_STORAGE_KEY = 'my247ed.media-library.folder';
    const VIEW_MODE_STORAGE_KEY = 'my247ed.media-library.viewMode';
    const SORT_BY_STORAGE_KEY = 'my247ed.media-library.sortBy';
    const TYPE_FILTER_STORAGE_KEY = 'my247ed.media-library.typeFilter';
    const VALID_VIEW_MODES = ['grid', 'list'];
    const VALID_SORTS = ['newest', 'oldest', 'name_asc', 'name_desc', 'size_asc', 'size_desc'];
    const VALID_TYPES = ['ALL', 'PHOTO', 'VIDEO', 'AUDIO', 'FILE'];

    function readPref(key, valid, fallback) {
        try {
            const raw = localStorage.getItem(key);
            if (raw === null) return fallback;
            const val = JSON.parse(raw);
            return valid.indexOf(val) !== -1 ? val : fallback;
        } catch (_) { return fallback; }
    }
    function savePref(key, value) {
        try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
    }

    export default {
        components: {CardContainer, CardFooter, SeparatorListItem, SearchListItem, ContainerListItem, SubheaderListItem, CardList, CardToolbar, CardBody, CardHeader, UploadQueue, DeleteConfirmModal, BulkActionBar, FolderTree, FileDetailsPanel, RecycleScroller},
        props: ['type'],
        data: function () {
            return {
                view: 'LIBRARY',        // LIBRARY | TRASH
                typeFilter: readPref(TYPE_FILTER_STORAGE_KEY, VALID_TYPES, 'ALL'),  // forced onto `type` prop if present
                files: [],
                providers: [],
                provider: null,
                orgProvider: false,
                allFolders: [],
                folder: 'all',          // 'all' | 'null' | folder id
                loaded: false,
                search: '',
                uploadVisible: false,
                deleteTarget: null,
                draggingItemIds: [],
                draggingFolderId: null,
                viewMode: readPref(VIEW_MODE_STORAGE_KEY, VALID_VIEW_MODES, 'grid'),
                sortBy: readPref(SORT_BY_STORAGE_KEY, VALID_SORTS, 'newest'),
                containerWidth: 0,
                gridCellWidth: GRID_CELL_WIDTH,
                gridRowHeight: GRID_ROW_HEIGHT,
                listRowHeight: LIST_ROW_HEIGHT,
                scrollerBuffer: SCROLLER_BUFFER,
                selectedIds: [],
                bulkWorking: false,
                bulkDownloading: false,
                deletedFiles: [],
                trashLoaded: false,
                trashLoading: false,
                libraryLoading: false,
                pptxExtracting: false,
                pptxExtractingName: '',
                pptxUploadPercent: null,
                transloading: false,
                detailsTarget: null
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            isTrashView() {
                return this.view === 'TRASH';
            },
            sourceFiles() {
                return this.isTrashView ? this.deletedFiles : this.files;
            },
            foldersById() {
                const map = {};
                this.folders.forEach(f => { map[f.id] = f; });
                return map;
            },
            media() {
                const matchesSearch = (m) =>
                    this.search === '' || (m.title || '').toLowerCase().includes(this.search.toLowerCase());

                const matchesFolder = (m) => {
                    if (this.folder === 'all') return true;
                    if (this.folder === 'null') return m.folder_id === null;
                    return m.folder_id === Number(this.folder);
                };

                // In picker mode the `type` prop pins the type filter; in trash the type filter is ignored.
                const activeType = this.type ? this.type : this.typeFilter;
                const typeMatches = (m) => this.isTrashView || activeType === 'ALL' || m.type === activeType;

                const result = this.sourceFiles.filter(m =>
                    typeMatches(m) && matchesFolder(m) && matchesSearch(m) && m.content_provider_id === this.provider
                );

                return this.sortMedia(result);
            },
            folders() {
                return this.allFolders.filter(f => !this.provider || f.content_provider_id === this.provider);
            },
            folderFileCounts() {
                // Direct file count per folder (not recursive). Useful for spotting
                // folders that actually contain files vs. ones holding only subfolders.
                const map = {};
                this.files.forEach(f => {
                    if (!f.folder_id) return;
                    map[f.folder_id] = (map[f.folder_id] || 0) + 1;
                });
                return map;
            },
            viewSubtitle() {
                if (this.isTrashView) return 'Trash';
                const parts = [];
                const typeLabel = { ALL: '', PHOTO: 'Photos', VIDEO: 'Videos', AUDIO: 'Audio', FILE: 'Documents' }[this.typeFilter] || '';
                if (typeLabel) parts.push(typeLabel);
                if (this.folder === 'all') parts.push('All Folders');
                else if (this.folder === 'null') parts.push('Unsorted');
                else {
                    const f = this.foldersById[this.folder];
                    parts.push(f ? f.title : 'Folder');
                }
                return parts.join(' · ');
            },
            gridColumns() {
                if (!this.containerWidth) return 0;
                return Math.max(1, Math.floor(this.containerWidth / GRID_CELL_WIDTH));
            },
            selectionEnabled() {
                return !this.type;
            },
            toolsBusy() {
                return this.pptxExtracting || this.transloading;
            },
            toolsBusyLabel() {
                if (this.pptxExtracting) return 'Extracting…';
                if (this.transloading) return 'Transloading…';
                return '';
            },
            visibleIds() {
                return this.media.map(m => m.id);
            },
            selectedCount() {
                return this.selectedIds.length;
            },
            allVisibleSelected() {
                if (!this.media.length) return false;
                return this.visibleIds.every(id => this.selectedIds.indexOf(id) !== -1);
            },
            someVisibleSelected() {
                if (!this.selectedIds.length || !this.media.length) return false;
                return !this.allVisibleSelected && this.visibleIds.some(id => this.selectedIds.indexOf(id) !== -1);
            }
        },

        methods: {
            loadLibrary: function (force) {
                this.$emit('loading', true);
                this.libraryLoading = true;
                async.series([
                    (cb) => {
                        http.get('library', {force}).then(response => {
                            this.allFolders = response.data.folders;
                            this.files = response.data.files;
                            cb();
                        }).catch(cb);
                    },
                    (cb) => {
                        if (this.providers.length === 0) {
                            http.get('organizations/self', {force: true}).then(response => {
                                this.providers = response.data.content_providers;
                                console.log('default provider: ', this.providers[0].id);
                                console.log('current user provider: ', this.currentUser.contentProviderId);

                                this.provider = this.currentUser.contentProviderId;

                                if (this.currentUser.contentProviderId === this.providers[0].id) {
                                    this.orgProvider = true;
                                }
                                console.log('set provider: ', this.provider, this.orgProvider);
                                cb();
                            }).catch(cb);
                        } else {
                            cb();
                        }
                    }
                ], err => {
                    if (err) console.log(err);
                    this.loaded = true;
                    this.libraryLoading = false;
                    this.$emit('loading', false);
                    this.applySavedFolder();
                })
            },
            newFolder: function (parentId) {
                const pid = (typeof parentId === 'number') ? parentId : null;
                let title = window.prompt(pid ? 'New Subfolder Title' : 'New Folder Title', 'Untitled Folder');
                if (!title) return;
                const body = { title };
                if (pid) body.parent_folder_id = pid;
                if (this.provider) body.content_provider_id = this.provider;
                http.post('library/folder', body).then(response => {
                    if (response && response.data && response.data.id) {
                        this.allFolders.push(response.data);
                    } else {
                        this.loadLibrary(true);
                    }
                }, err => {
                    console.log(err);
                    this.$buefy.toast.open({ message: 'Could not create folder', type: 'is-danger' });
                });
            },
            deleteFolderFromTree(folder) {
                if (!folder) return;
                if (!window.confirm(`Delete folder "${folder.title}"? Any files inside will move to Unsorted, and subfolders will move up to this folder's parent.`)) return;
                // Optimistic: mirror backend behavior locally — reparent child
                // folders up one level, set direct files to Unsorted, drop the folder.
                const newParentForChildren = folder.parent_folder_id || null;
                const foldersSnapshot = this.allFolders.slice();
                const filesSnapshot = this.files.slice();
                this.allFolders = this.allFolders
                    .map(f => f.parent_folder_id === folder.id ? { ...f, parent_folder_id: newParentForChildren } : f)
                    .filter(f => f.id !== folder.id);
                this.files = this.files.map(f => f.folder_id === folder.id ? { ...f, folder_id: null } : f);
                if (Number(this.folder) === folder.id) this.folder = 'all';
                http.delete('library/folder/' + folder.id).catch(err => {
                    console.log(err);
                    this.allFolders = foldersSnapshot;
                    this.files = filesSnapshot;
                    this.$buefy.toast.open({ message: 'Could not delete folder', type: 'is-danger' });
                });
            },
            renameFolderPrompt(folder) {
                const title = window.prompt('Rename folder', folder.title || '');
                if (title === null) return;
                const trimmed = title.trim();
                if (!trimmed || trimmed === folder.title) return;
                const previousTitle = folder.title;
                this.allFolders = this.allFolders.map(f => f.id === folder.id ? { ...f, title: trimmed } : f);
                http.put('library/folder/' + folder.id, { title: trimmed }).catch(err => {
                    console.log(err);
                    this.allFolders = this.allFolders.map(f => f.id === folder.id ? { ...f, title: previousTitle } : f);
                    this.$buefy.toast.open({ message: 'Could not rename folder', type: 'is-danger' });
                });
            },
            onTreeSelect({ view, folder }) {
                if (view !== this.view) this.view = view;
                this.folder = folder;
            },
            onFolderDragStart({ folderId }) {
                this.draggingFolderId = folderId;
                this.draggingItemIds = [];
            },
            onFolderDragEnd() {
                this.draggingFolderId = null;
            },
            onItemDragStart(ev, item) {
                // If the item is in the current selection, drag the whole selection;
                // otherwise drag just this one item (and don't mutate selection).
                const ids = this.isSelected(item.id) && this.selectedIds.length
                    ? this.selectedIds.slice()
                    : [item.id];
                this.draggingItemIds = ids;
                this.draggingFolderId = null;
                try {
                    ev.dataTransfer.effectAllowed = 'move';
                    ev.dataTransfer.setData('text/plain', `library:${ids.join(',')}`);
                } catch (_) {}
            },
            onItemDragEnd() {
                this.draggingItemIds = [];
            },
            onTreeDrop({ folderId }) {
                // Folder being reparented
                if (this.draggingFolderId !== null) {
                    const movingId = this.draggingFolderId;
                    this.draggingFolderId = null;
                    if (folderId === movingId) return;
                    // Guard: prevent moving a folder into its own descendant (backend also checks)
                    if (folderId !== null && this.isDescendantFolder(folderId, movingId)) {
                        this.$buefy.toast.open({ message: 'Cannot move a folder into its own subtree', type: 'is-warning' });
                        return;
                    }
                    // Optimistic reparent; revert on failure.
                    const prevParent = (this.foldersById[movingId] || {}).parent_folder_id || null;
                    this.allFolders = this.allFolders.map(f => f.id === movingId ? { ...f, parent_folder_id: folderId } : f);
                    http.put('library/folder/' + movingId, { parent_folder_id: folderId }).catch(err => {
                        console.log(err);
                        this.allFolders = this.allFolders.map(f => f.id === movingId ? { ...f, parent_folder_id: prevParent } : f);
                        this.$buefy.toast.open({ message: 'Could not move folder', type: 'is-danger' });
                    });
                    return;
                }
                // Media items being moved
                if (this.draggingItemIds.length) {
                    const ids = this.draggingItemIds.slice();
                    this.draggingItemIds = [];
                    http.post('library/bulk', { action: 'move', ids, folder_id: folderId }).then(() => {
                        const idSet = new Set(ids);
                        this.files = this.files.map(f => idSet.has(f.id) ? { ...f, folder_id: folderId } : f);
                        this.$buefy.toast.open({ message: ids.length + ' file' + (ids.length === 1 ? '' : 's') + ' moved', type: 'is-success' });
                    }).catch(err => {
                        console.error(err);
                        this.$buefy.toast.open({ message: 'Move failed', type: 'is-danger' });
                    });
                }
            },
            isDescendantFolder(candidateId, ancestorId) {
                // true if `candidateId` is ancestorId itself or anywhere in its subtree
                if (candidateId === ancestorId) return true;
                const stack = [ancestorId];
                const childrenOf = {};
                this.folders.forEach(f => {
                    const p = f.parent_folder_id || null;
                    if (!childrenOf[p]) childrenOf[p] = [];
                    childrenOf[p].push(f.id);
                });
                while (stack.length) {
                    const cur = stack.pop();
                    for (const child of (childrenOf[cur] || [])) {
                        if (child === candidateId) return true;
                        stack.push(child);
                    }
                }
                return false;
            },
            moveToFolder: function (media, folder) {
                http.put('library/' + media.id, {media_id: media.id, folder_id: folder.id}).then(response => {
                    this.loadLibrary(true);
                }, err => {
                    console.log(err);
                });
            },
            findFolderTitle: function (id) {
                let folder = this.folders.find(f => f.id === id);
                return folder ? folder.title : 'Unknown Folder!';
            },
            onUploaded(row) {
                if (!row || !row.id) return;
                // Avoid duplicate insert if the parent eventually re-fetches
                if (this.files.some(f => f.id === row.id)) return;
                this.files.unshift(row);
            },
            onItemClick(m) {
                // Picker mode wants the click to commit a selection; non-picker
                // mode opens the details slide-out instead.
                if (this.type) {
                    this.$emit('choose', m);
                } else {
                    this.detailsTarget = m;
                }
            },
            onFileUpdated(row) {
                if (!row || !row.id) return;
                this.files = this.files.map(f => f.id === row.id ? row : f);
                if (this.detailsTarget && this.detailsTarget.id === row.id) {
                    this.detailsTarget = row;
                }
            },
            requestDelete(file) {
                this.deleteTarget = file;
            },
            onDeleted(file) {
                this.files = this.files.filter(f => f.id !== file.id);
                if (this.trashLoaded) {
                    this.deletedFiles.unshift({ ...file, deleted_at: new Date().toISOString() });
                }
                this.deleteTarget = null;
                if (this.detailsTarget && this.detailsTarget.id === file.id) this.detailsTarget = null;
                this.$buefy.toast.open({ message: 'File deleted', type: 'is-success' });
            },
            loadDeleted(force) {
                if (this.trashLoading) return;
                if (this.trashLoaded && !force) return;
                this.trashLoading = true;
                http.get('library?deleted=only', { force: true }).then(response => {
                    this.deletedFiles = response.data.files || [];
                    this.trashLoaded = true;
                }).catch(err => {
                    console.error(err);
                    this.$buefy.toast.open({ message: 'Failed to load trash', type: 'is-danger' });
                }).finally(() => {
                    this.trashLoading = false;
                });
            },
            restoreFile(file) {
                http.post('library/' + file.id + '/restore').then(() => {
                    this.deletedFiles = this.deletedFiles.filter(f => f.id !== file.id);
                    const restored = { ...file, deleted_at: null, deleted_by: null };
                    if (!this.files.some(f => f.id === file.id)) this.files.unshift(restored);
                    this.$buefy.toast.open({ message: 'File restored', type: 'is-success' });
                }).catch(err => {
                    console.error(err);
                    this.$buefy.toast.open({ message: 'Restore failed', type: 'is-danger' });
                });
            },
            bulkRestore() {
                const ids = this.selectedIds.slice();
                if (!ids.length) return;
                this.bulkWorking = true;
                http.post('library/bulk', { action: 'restore', ids }).then(() => {
                    const idSet = new Set(ids);
                    const restored = this.deletedFiles
                        .filter(f => idSet.has(f.id))
                        .map(f => ({ ...f, deleted_at: null, deleted_by: null }));
                    this.deletedFiles = this.deletedFiles.filter(f => !idSet.has(f.id));
                    restored.forEach(r => {
                        if (!this.files.some(f => f.id === r.id)) this.files.unshift(r);
                    });
                    this.selectedIds = [];
                    this.$buefy.toast.open({ message: ids.length + ' files restored', type: 'is-success' });
                }).catch(err => {
                    console.error(err);
                    this.$buefy.toast.open({ message: 'Bulk restore failed', type: 'is-danger' });
                }).finally(() => {
                    this.bulkWorking = false;
                });
            },
            sortMedia(list) {
                let cmp;
                const toTime = (m) => m.created_at ? new Date(m.created_at).getTime() : 0;
                const toSize = (m) => Number(m.size || 0);
                const toTitle = (m) => (m.title || '').toLowerCase();
                switch (this.sortBy) {
                    case 'oldest':     cmp = (a, b) => toTime(a) - toTime(b); break;
                    case 'name_asc':   cmp = (a, b) => toTitle(a).localeCompare(toTitle(b)); break;
                    case 'name_desc':  cmp = (a, b) => toTitle(b).localeCompare(toTitle(a)); break;
                    case 'size_asc':   cmp = (a, b) => toSize(a) - toSize(b); break;
                    case 'size_desc':  cmp = (a, b) => toSize(b) - toSize(a); break;
                    case 'newest':
                    default:           cmp = (a, b) => toTime(b) - toTime(a); break;
                }
                return list.slice().sort(cmp);
            },
            measureContainer() {
                const el = this.$refs.scroller;
                if (el) this.containerWidth = el.clientWidth;
            },
            readSavedFolder() {
                try {
                    const raw = localStorage.getItem(FOLDER_STORAGE_KEY);
                    if (raw === null) return null;
                    return JSON.parse(raw);
                } catch (_) { return null; }
            },
            saveFolder() {
                try {
                    localStorage.setItem(FOLDER_STORAGE_KEY, JSON.stringify(this.folder));
                } catch (_) {}
            },
            applySavedFolder() {
                // Apply a stashed folder pref, but only if it still resolves. Must run after
                // `allFolders` is populated so we can validate numeric ids.
                if (this._initialFolder === undefined) return;
                const saved = this._initialFolder;
                this._initialFolder = undefined;
                if (saved === 'all' || saved === 'null') this.folder = saved;
                else if (typeof saved === 'number' && this.allFolders.some(f => f.id === saved)) this.folder = saved;
            },
            attachResizeObserver() {
                const el = this.$refs.scroller;
                if (!el) return;
                this.measureContainer();
                if (window.ResizeObserver) {
                    if (this._resizeObserver) this._resizeObserver.disconnect();
                    this._resizeObserver = new ResizeObserver(() => this.measureContainer());
                    this._resizeObserver.observe(el);
                } else {
                    if (this._onResize) window.removeEventListener('resize', this._onResize);
                    this._onResize = () => this.measureContainer();
                    window.addEventListener('resize', this._onResize);
                }
            },
            isSelected(id) {
                return this.selectedIds.indexOf(id) !== -1;
            },
            toggleSelection(id) {
                const idx = this.selectedIds.indexOf(id);
                if (idx === -1) this.selectedIds.push(id);
                else this.selectedIds.splice(idx, 1);
            },
            toggleSelectAllVisible() {
                if (this.allVisibleSelected) {
                    // deselect just the visible ones, leave anything else alone
                    this.selectedIds = this.selectedIds.filter(id => this.visibleIds.indexOf(id) === -1);
                } else {
                    const existing = new Set(this.selectedIds);
                    this.visibleIds.forEach(id => existing.add(id));
                    this.selectedIds = Array.from(existing);
                }
            },
            clearSelection() {
                this.selectedIds = [];
            },
            bulkDelete() {
                const ids = this.selectedIds.slice();
                if (!ids.length) return;
                this.$buefy.dialog.confirm({
                    title: 'Delete ' + ids.length + ' files?',
                    message: 'These files will be moved to the trash. Usage detection is not run in bulk — any of these files that are referenced elsewhere (courses, lessons, sessions) will show as broken media until restored.',
                    confirmText: 'Delete ' + ids.length + ' Files',
                    type: 'is-danger',
                    onConfirm: () => {
                        this.bulkWorking = true;
                        http.post('library/bulk', { action: 'delete', ids }).then(() => {
                            const idSet = new Set(ids);
                            this.files = this.files.filter(f => !idSet.has(f.id));
                            this.selectedIds = [];
                            this.$buefy.toast.open({ message: ids.length + ' files deleted', type: 'is-success' });
                        }).catch(err => {
                            console.error(err);
                            this.$buefy.toast.open({ message: 'Bulk delete failed', type: 'is-danger' });
                        }).finally(() => {
                            this.bulkWorking = false;
                        });
                    }
                });
            },
            openPptxPicker() {
                if (this.toolsBusy) return;
                this.$refs.pptxInput && this.$refs.pptxInput.click();
            },
            openTransloadPrompt() {
                if (this.toolsBusy) return;
                this.$buefy.dialog.prompt({
                    title: 'Transload File',
                    message: 'Enter the URL of the file you want to copy into the library. Supports images, videos, PDFs, etc.',
                    inputAttrs: {
                        placeholder: 'https://example.com/file.pdf',
                        type: 'url',
                        maxlength: 2000
                    },
                    trapFocus: true,
                    confirmText: 'Transload',
                    onConfirm: (url) => this.transload((url || '').trim())
                });
            },
            transload(urlString) {
                if (!urlString) return;
                this.transloading = true;
                const body = { url: urlString };
                if (this.folder !== 'all' && this.folder !== 'null') body.folder_id = this.folder;
                if (this.provider) body.content_provider_id = this.provider;

                http.post('library/transload', body, { timeout: 10 * 60 * 1000 }).then(response => {
                    if (response && response.data && response.data.id) {
                        this.onUploaded(response.data);
                        this.$buefy.toast.open({ message: 'File transloaded', type: 'is-success' });
                    } else {
                        this.$buefy.toast.open({ message: 'Transload completed but no file returned', type: 'is-warning' });
                    }
                }).catch(err => {
                    console.error(err);
                    const msg = (err.response && err.response.data && err.response.data.error) || 'Transload failed';
                    this.$buefy.toast.open({ message: msg, type: 'is-danger', duration: 6000 });
                }).finally(() => {
                    this.transloading = false;
                });
            },
            onPptxFileSelected(event) {
                const file = event.target.files && event.target.files[0];
                event.target.value = ''; // allow picking the same file again later
                if (!file) return;
                this.extractPptx(file);
            },
            extractPptx(file) {
                this.pptxExtracting = true;
                this.pptxExtractingName = file.name;
                this.pptxUploadPercent = 0;
                const formData = new FormData();
                formData.append('file', file);
                if (this.folder !== 'all' && this.folder !== 'null') formData.append('folder_id', this.folder);
                if (this.provider) formData.append('content_provider_id', this.provider);

                http.post('library/extract-pptx', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    // extraction itself can be slow; give it plenty of headroom
                    timeout: 10 * 60 * 1000,
                    onUploadProgress: (progressEvent) => {
                        if (!progressEvent.total) return;
                        const pct = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        this.pptxUploadPercent = pct;
                        if (pct >= 100) this.pptxUploadPercent = null; // server is now processing
                    }
                }).then(response => {
                    const created = (response.data && response.data.files) || [];
                    created.forEach(row => this.onUploaded(row));
                    const slideCount = (response.data && response.data.slideCount) || (created.length > 0 ? created.length - 1 : 0);
                    this.$buefy.toast.open({
                        message: `Extracted ${slideCount} slide${slideCount === 1 ? '' : 's'} from ${file.name}`,
                        type: 'is-success',
                        duration: 5000
                    });
                }).catch(err => {
                    console.error(err);
                    const msg = (err.response && err.response.data && err.response.data.error) || 'Extraction failed';
                    this.$buefy.toast.open({ message: msg, type: 'is-danger', duration: 6000 });
                }).finally(() => {
                    this.pptxExtracting = false;
                    this.pptxExtractingName = '';
                    this.pptxUploadPercent = null;
                });
            },
            downloadFile(file) {
                http.get('library/' + file.id + '/download', { responseType: 'blob' }).then(response => {
                    let filename = file.title || `file-${file.id}`;
                    const cd = response.headers && response.headers['content-disposition'];
                    if (cd) {
                        const match = /filename="?([^"]+)"?/.exec(cd);
                        if (match && match[1]) filename = match[1];
                    }
                    const blob = new Blob([response.data], {
                        type: (response.headers && response.headers['content-type']) || 'application/octet-stream'
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                }).catch(err => {
                    console.error(err);
                    this.$buefy.toast.open({ message: 'Download failed', type: 'is-danger' });
                });
            },
            bulkDownload() {
                const ids = this.selectedIds.slice();
                if (!ids.length) return;
                this.bulkDownloading = true;
                http.post('library/bulk/download', { ids }, { responseType: 'blob' }).then(response => {
                    // Prefer the server's Content-Disposition filename, fall back to a timestamped name.
                    let filename = `media-bundle-${Date.now()}.zip`;
                    const cd = response.headers && response.headers['content-disposition'];
                    if (cd) {
                        const match = /filename="?([^"]+)"?/.exec(cd);
                        if (match && match[1]) filename = match[1];
                    }
                    const blob = new Blob([response.data], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    this.$buefy.toast.open({ message: `Downloaded ${ids.length} file${ids.length === 1 ? '' : 's'}`, type: 'is-success' });
                }).catch(err => {
                    console.error(err);
                    this.$buefy.toast.open({ message: 'Download failed', type: 'is-danger' });
                }).finally(() => {
                    this.bulkDownloading = false;
                });
            },
            bulkMove(folderId) {
                const ids = this.selectedIds.slice();
                if (!ids.length) return;
                this.bulkWorking = true;
                http.post('library/bulk', { action: 'move', ids, folder_id: folderId }).then(() => {
                    const idSet = new Set(ids);
                    this.files = this.files.map(f => idSet.has(f.id) ? { ...f, folder_id: folderId } : f);
                    this.selectedIds = [];
                    this.$buefy.toast.open({ message: ids.length + ' files moved', type: 'is-success' });
                }).catch(err => {
                    console.error(err);
                    this.$buefy.toast.open({ message: 'Bulk move failed', type: 'is-danger' });
                }).finally(() => {
                    this.bulkWorking = false;
                });
            }
        },
        watch: {
            provider: function (newVal, oldVal) {
                // Skip the reset on the initial assignment (null/undefined → first value);
                // otherwise we'd clobber the folder restored by applySavedFolder on mount.
                if (oldVal === null || oldVal === undefined) return;
                this.folder = 'all';
            },
            loaded(val) {
                if (val) this.$nextTick(() => this.attachResizeObserver());
            },
            view(newView, oldView) {
                if (newView !== oldView) {
                    this.selectedIds = [];
                    this.detailsTarget = null;
                }
                if (newView === 'TRASH') this.loadDeleted();
            },
            typeFilter(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.selectedIds = [];
                    this.detailsTarget = null;
                }
                savePref(TYPE_FILTER_STORAGE_KEY, newVal);
            },
            folder(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.selectedIds = [];
                    this.detailsTarget = null;
                }
                this.saveFolder();
            },
            viewMode(newVal) {
                savePref(VIEW_MODE_STORAGE_KEY, newVal);
            },
            sortBy(newVal) {
                savePref(SORT_BY_STORAGE_KEY, newVal);
            }
        },
        mounted() {
            // Stash the saved folder BEFORE loadLibrary runs: the provider watcher
            // will reset `folder` to 'all' mid-load, which would otherwise clobber
            // whatever we read back from storage.
            this._initialFolder = this.readSavedFolder();
            this.loadLibrary();
        },
        beforeDestroy() {
            if (this._resizeObserver) this._resizeObserver.disconnect();
            else if (this._onResize) window.removeEventListener('resize', this._onResize);
        }
    };
</script>

<style lang="scss">
    .pptx-hidden-input {
        display: none;
    }

    .media-browser {
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        height: 100%;
        min-height: 500px;

        .upload-progress {
            background: #f3f3f3;
            border-bottom: 1px solid #dee2e6;
        }

        .side-menu {
            min-width: 290px;
            max-width: 320px;
            border-right: 1px solid #e7e7e7;
            display: flex;
            flex-direction: column;
            background-color: #fff;
            background-image: linear-gradient(white, #f6f6f6);
        }

        .view {
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
            overflow: hidden;
            position: relative;

            &.view-loading {
                align-items: center;
                justify-content: center;
                color: #6b7280;

                i {
                    color: #3c6691;
                }
            }

            .select-count {
                display: inline-flex;
                align-items: center;
            }

            .card-image {
                background-color: white;
            }

            .media-scroller {
                flex: 1;
                min-height: 0;
                position: relative;

                .scroller {
                    height: 100%;
                    padding: 15px;
                }
            }

            .view-toggle {
                flex-wrap: nowrap;
                margin: 0;

                .button {
                    height: 2.5em;
                }
            }

            .tool-banner {
                display: flex;
                align-items: center;
                gap: 0.25rem;
                padding: 0.75rem 1rem;
                background: #fef7e6;
                border-bottom: 1px solid #f4e0a8;
                color: #8a6d1a;
                position: relative;

                > i.fa-spinner {
                    font-size: 1.1rem;
                }

                > div { flex: 1; }

                .tool-banner-progress {
                    width: 180px;
                    margin-left: 0.75rem;
                    margin-bottom: 0;
                }
            }

            .media-card {
                position: relative;
                width: 260px;
                height: 340px;
                margin: 5px;
                overflow: hidden;
                background-color: #f8f8f8;
                border: 1px solid #e0e0e0;
                transition: border-color 120ms, box-shadow 120ms, opacity 120ms;

                &.is-selected {
                    border-color: #336699;
                    box-shadow: 0 0 0 2px rgba(51, 102, 153, 0.35);
                }

                &.is-active {
                    border-color: #f0a500;
                    box-shadow: 0 0 0 2px rgba(240, 165, 0, 0.4);
                }

                &.is-dragging {
                    opacity: 0.5;
                }

                .card-image {
                    position: relative;
                }

                .card-select-btn {
                    position: absolute;
                    top: 6px;
                    right: 6px;
                    z-index: 2;
                    width: 28px;
                    height: 28px;
                    padding: 0;
                    border: none;
                    border-radius: 4px;
                    background: rgba(0, 0, 0, 0.55);
                    color: #ffffff;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 120ms, color 120ms;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);

                    i {
                        font-size: 1rem;
                    }

                    &:hover {
                        background: rgba(0, 0, 0, 0.75);
                    }

                    &.is-active {
                        background: #336699;
                        color: #ffffff;
                    }
                }

                .card-content {
                    border-top: 1px solid #e7e7e7;
                }

                .truncate {
                    width: 220px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
            }

            .media-row {
                display: flex;
                align-items: center;
                gap: 0.75rem;
                padding: 0.5rem 0.75rem;
                // inset shadow rather than border-bottom: doesn't add to the
                // 64px row height, so it paints cleanly across every virtualized
                // row regardless of the scroller's translateY rounding.
                box-shadow: inset 0 -1px 0 #eee;
                background: #fff;
                height: 100%;

                &.is-clickable {
                    cursor: pointer;

                    &:hover {
                        background: #f6f9fc;
                    }
                }

                &.is-selected {
                    background: #eaf2fa;
                }

                &.is-active {
                    background: #fff4dc;
                    box-shadow: inset 3px 0 0 #f0a500, inset 0 -1px 0 #eee;
                }

                &.is-dragging {
                    opacity: 0.5;
                }

                .media-row-select {
                    flex: 0 0 auto;
                }

                .media-row-thumb {
                    width: 48px;
                    height: 48px;
                    flex: 0 0 auto;
                    background: #f3f3f3;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;

                    img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    i {
                        color: #8a99ae;
                        font-size: 1.25rem;
                    }
                }

                .media-row-title {
                    flex: 1;
                    min-width: 0;

                    .truncate {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }

                .media-row-meta {
                    flex: 0 0 auto;
                    display: flex;
                    gap: 0.75rem;
                    padding-right: 0.5rem;
                }

                .media-row-actions {
                    flex: 0 0 auto;
                }
            }

            .empty-state {
                padding: 3rem 1rem;
            }
        }
    }

</style>
